import { useSuspenseQuery } from "@tanstack/react-query";
import { METRIC_STALE_TIME } from "../constants";
import { DEFAULT_CAMPAIGN } from "../constants/campaignPerformance";
import type { CampaignImpactResponse } from "../types";
import { formatLocalDate } from "../utils/date";
import { api } from "../utils/apiClient";

type CampaignImpactRequest = {
  period: string;
  campaign: string;
};

const CAMPAIGN_IMPACT_ENDPOINT = "/api/v1/campaigns/impact";

const parsePeriodKey = (period: string): { periodType: string; periodValue: string } | undefined => {
  const idx = period.indexOf("_");
  if (idx === -1) return undefined;
  return { periodType: period.slice(0, idx), periodValue: period.slice(idx + 1) };
};

export const fetchCampaignImpact = async (
  endpoint: string,
  { period, campaign }: CampaignImpactRequest
): Promise<CampaignImpactResponse> => {
  const searchParams = new URLSearchParams();
  const parsed = parsePeriodKey(period);
  if (parsed) {
    searchParams.set("period_type", parsed.periodType);
    searchParams.set("period_value", parsed.periodValue);
  }
  searchParams.set("campaign", campaign);

  const res = await api.get(`${endpoint}?${searchParams.toString()}`);
  if (!res.ok) throw new Error(`Failed to load campaign impact: ${res.status}`);
  return res.json();
};

export type CampaignImpactQueryParams = {
  period: string;
};

/**
 * Today's local date as `YYYY-MM-DDT00:00:00.000Z`. Included in the React Query key so the
 * cache invalidates once per calendar day — the backend derives the reporting window from
 * "today", so a stale entry from yesterday must not be served after midnight.
 */
const todayKey = (): string => formatLocalDate(new Date());

export const useCampaignImpactQuery = ({ period }: CampaignImpactQueryParams) => {
  const request: CampaignImpactRequest = {
    period,
    campaign: DEFAULT_CAMPAIGN,
  };

  return useSuspenseQuery({
    queryKey: [CAMPAIGN_IMPACT_ENDPOINT, period, DEFAULT_CAMPAIGN, todayKey()],
    queryFn: () => fetchCampaignImpact(CAMPAIGN_IMPACT_ENDPOINT, request),
    select: (data: CampaignImpactResponse) => data,
    staleTime: METRIC_STALE_TIME,
  });
};
